// Copyright (c) 2025 Audere, All Rights Reserved

import { useCallback, useState } from "react";
import { useTranslation } from "react-i18next";

import { Box, Divider, Stack, Typography } from "@mui/material";
import TestImageUploadProgress from "./TestUploadImageProgress";
import { createStyleSheet } from "../utils/styles";
import type { EvaluationSetupProps, ProjectModel, TestData } from "../types/projects";
import { useStartLabelingMutation } from "../features/data/projectApiSlice";
import { getStatusIcon } from "./StatusIcon";
import UploadImage from "../../assets/upload_image.svg";
import TestFileUpload from "./TestFileUpload";

const EvaluationSetup = (props: EvaluationSetupProps) => {
  const { projectDetails, latestModel } = props;
  const projectData = projectDetails?.project;
  const { t } = useTranslation("EvaluationSetup");
  const [startLabeling] = useStartLabelingMutation();

  const [selectedTestId, setSelectedTestId] = useState("");

  const handleProceed = useCallback(
    (rdt_uid: string) => {
      startLabeling({ rdt_uid });
    },
    [startLabeling]
  );

  const getStepsFromModel = (model: ProjectModel) => [
    {
      label: t("trainingYourModel"),
      status: model?.training?.status,
    },
    {
      label: t("validating"),
      status: model?.validation?.status,
    },
  ];
  const handleAddMoreImages = useCallback((testId: string) => {
    setSelectedTestId(testId);
  }, []);
  const handleContinue = () => {
    setSelectedTestId("");
  };

  const steps = latestModel ? getStepsFromModel(latestModel) : [];

  return (
    <Box sx={styles.container} data-testid="evaluation-setup-root">
      <Box>
        <Box sx={styles.header} data-testid="evaluation-header">
          <img src={UploadImage} alt={t("uploadImages")} data-testid="upload-image" />
          <Typography fontWeight={600} fontSize={18} mt={1} data-testid="upload-title">
            {t("uploadImages")}
          </Typography>
        </Box>
        <Divider />
        <Box sx={styles.evaluationBody} data-testid="evaluation-body">
          {projectData?.rdts?.map((test: TestData) => (
            <Box mt={2} key={test.rdt_uid} data-testid="test-upload-progress">
              <TestImageUploadProgress
                testData={test}
                onProceed={handleProceed}
                onAddMoreImages={handleAddMoreImages}
              />
              {selectedTestId === test.rdt_uid && (
                <TestFileUpload
                  isThumbnailRequired={false}
                  onContinue={handleContinue}
                  onCancel={handleContinue}
                />
              )}
            </Box>
          ))}
          {steps.length > 0 && (
            <Stack spacing={1.5} mt={2}>
              {steps.map(step => (
                <Box key={step.label} sx={styles.modelStepList}>
                  {getStatusIcon(step.status)}
                  <Typography ml={1}>{step.label}</Typography>
                </Box>
              ))}
            </Stack>
          )}
        </Box>
      </Box>
    </Box>
  );
};

export default EvaluationSetup;

const styles = createStyleSheet({
  container: {
    background: "white",
    mt: 2,
    width: "60%",
  },
  header: {
    display: "flex",
    alignItems: "center",
    gap: 1,
    padding: "20px",
  },
  rightPaneInnerContainer: {
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    padding: "10px",
    flexDirection: "column",
  },
  italicText: {
    fontStyle: "italic",
  },
  modelStepList: {
    display: "flex",
    alignItems: "center",
  },
  evaluationBody: {
    p: "20px",
  },
});
